import React, { useEffect, useState, memo, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import useMenuData from '../../../assets/hooks/useMenuData';
import FooterBottom from './FooterBottom';
import { renderFooterGlowBadgeLeading } from './footerGlowBadgeLeading';
import {
  buildGlowFooterSectionsFromMenu,
  isExternalFooterHref,
  sliceGlowFooterSectionsByMenuRange
} from './buildGlowFooterMenuSections';
import { getFooterStatsFallback } from '../../../assets/lib/api/commApi';
import { fetchFooterContentDocumentWithFallback } from '../../../assets/lib/api/footerContentApi';
import {
  mergeFooterDataWithDefaults,
  normalizeFooterDataForRuntime,
  shouldLoadFooterPartnerLogoImageUrl
} from '../../../assets/lib/footer/footerContentNormalize';
import { attachFooterContactIcons } from '../../../assets/lib/footer/footerContactIcons';
import { getDefaultCopyright } from '../pages/system/FooterAreaManager';
import { shouldSkipMenuFetch } from '../../../assets/utils/menu/menuFetchPolicy';
import { i18nLangToThemeLocale } from '../../../assets/utils/comm/languageConstants';

// 테마별 CSS 파일 import
import './theme/footer-theme-simple.css';
import './theme/footer-theme-modern.css';
import './theme/footer-theme-vibrant.css';
import './theme/footer-theme-glow.css';

const isFooterPartnerLogoUrlSafe = shouldLoadFooterPartnerLogoImageUrl;

/** GLOW 푸터 빠른 링크 기본값 (CMS에서 빠른 링크를 비우지 않은 경우 저장값 우선) */
const DEFAULT_GLOW_QUICK_LINK_SECTION = {
  title: '빠른 링크',
  iconType: 'arrow',
  items: [
    { text: '한국외국어대학교', link: 'https://www.hufs.ac.kr/', openInNewTab: true },
    { text: 'RISE 사업단', link: 'https://seoulrise.hufs.ac.kr/rise/17281/subview.do', openInNewTab: true },
    { text: '이용약관', link: '/terms' },
    { text: '개인정보처리방침', link: '/privacy' }
  ]
};

function isGlowQuickLinkSection(section) {
  const t = section?.title;
  return t === '빠른 링크' || t === 'Quick links';
}

/** CMS 빠른 링크에 약관 항목이 빠져 있어도 푸터에 고정 노출 */
const GLOW_QUICK_LEGAL_ITEMS = [
  { text: '이용약관', link: '/terms' },
  { text: '개인정보처리방침', link: '/privacy' }
];

function mergeLegalLinksIntoGlowQuickSection(section) {
  if (!section || typeof section !== 'object') return section;
  const items = Array.isArray(section.items) ? [...section.items] : [];
  for (const legal of GLOW_QUICK_LEGAL_ITEMS) {
    const has = items.some((it) => it?.link === legal.link || it?.text === legal.text);
    if (!has) items.push({ ...legal });
  }
  return { ...section, items };
}

/** GLOW 푸터 우측 링크 열 (제목 + 목록) */
function GlowFooterLinkColumn({ section }) {
  if (!section?.items?.length) return null;
  return (
    <div className="footer-glow-link-column">
      <h4 className="footer-links-section-title">{section.title}</h4>
      <ul className="footer-links-section-list">
        {section.items.map((item, index) => {
          const href = item.link || '#';
          const external = isExternalFooterHref(href);
          const showExtIcon = section.iconType === 'external' || external;
          const inner = (
            <>
              {showExtIcon ? (
                <span className="glow-footer-link-external">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" x2="21" y1="14" y2="3"></line>
                  </svg>
                </span>
              ) : (
                <span className="glow-footer-link-arrow">→</span>
              )}
              {item.text}
            </>
          );
          return (
            <li key={index} className="footer-links-section-list-item">
              {external ? (
                <a
                  href={href}
                  className="footer-links-section-link glow-footer-link"
                  {...(item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {inner}
                </a>
              ) : (
                <Link to={href} className="footer-links-section-link glow-footer-link">
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Footer 컴포넌트
 * 원본: footer.html
 *
 * footerContent JSON 데이터 구조로 동적 렌더링
 * - logo: 로고 이미지 및 링크
 * - about: 설명 및 하단 이미지 리스트 (원본 구조: { type: "bottom-img-list", list: [...] })
 * - widgets: 위젯 배열 (list 또는 address 타입)
 * - copyright: 저작권 텍스트
 * 성능 최적화: React.memo로 메모이제이션
 */

// 모든 테마에서 공통으로 사용할 Footer 데이터 (JSX 상단에 선언)
const defaultFooterData = {
  // 통계 섹션 데이터 (GLOW 테마 전용) — 숫자/라벨은 API 응답으로 덮어씀. 기본값은 getFooterStatsFallback()과 동일
  statistics: getFooterStatsFallback(),
  // GLOW Platform 섹션 데이터
  glowPlatform: {
    logo: {
      size: 49,
      backgroundColor: "rgb(147, 51, 234)",
      border: "1px solid #ffffff33",
      letter: "G",
      fontSize: "1.5rem"
    },
    title: "GLOW Platform",
    subtitle: "지산학 R&D 플랫폼",
    description: "지산학 R&D 전주기 엑셀러레이팅 플랫폼 GLOW 는 기업과 대학, 구기관을 AI 기반 매칭으로 연결하여 혁신적인 R&D 협업 생태계를 구축합니다.",
    badges: [
      {
        text: 'RISE 사업',
        backgroundColor: '#065f46',
        borderColor: '#05df72',
        color: '#05df72',
        dotColor: '#4ade80',
        icon: '🚀'
      },
      {
        text: '한국외국어대학교',
        backgroundColor: '#0d1b3a',
        borderColor: '#51a2ff',
        color: '#51a2ff',
        dotColor: '#51a2ff',
        icon: 'ti-world'
      }
    ],
    /** 배지 하단 로고 행 — 라벨 없이 이미지만 (imageUrl, alt, link · 링크 시 새 창) */
    partnerLogos: []
  },
  /**
   * 링크 섹션 목록 (DB 연동 시 0개~N개로 변경 가능)
   * 각 섹션: { title, items: [{ text, link }], iconType?: 'arrow' | 'external' }
   */
  linkSections: [DEFAULT_GLOW_QUICK_LINK_SECTION],
  // 문의 & 지원 데이터
  contact: {
    title: "문의 & 지원",
    items: [
      {
        icon: (
          <svg style={{ color: '#51a2ff' }} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-map-pin h-5 w-5 text-blue-400 dark:text-blue-300 mt-0.5 flex-shrink-0 group-hover:text-blue-300 dark:group-hover:text-blue-200 transition-colors" aria-hidden="true">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        ),
        type: "address",
        title: "한국외국어대학교 G-RISE 사업단",
        content: "17053 경기도 용인시 처인구 모현읍 외대로 81 백년관 3층 경기RISE사업단"
      },
      {
        icon: (
          <svg style={{ color: '#51a2ff' }} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-phone h-5 w-5 text-blue-400 dark:text-blue-300 flex-shrink-0 group-hover:text-blue-300 dark:group-hover:text-blue-200 transition-colors" aria-hidden="true">
            <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path>
          </svg>
        ),
        type: "phone",
        content: "031-8020-5048"
      },
      {
        icon: (
          <svg style={{ color: '#51a2ff' }} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail h-5 w-5 text-blue-400 dark:text-blue-300 flex-shrink-0 group-hover:text-blue-300 dark:group-hover:text-blue-200 transition-colors" aria-hidden="true">
            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
          </svg>
        ),
        type: "E-mail",
        content: "glow-rise@hufs.ac.kr"
      },
      {
        icon: (
          <svg style={{ color: '#51a2ff' }} xmlns="http://www.w3.org/2000/svg" width="17.5" height="17.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clock h-5 w-5 text-blue-400 dark:text-blue-300 flex-shrink-0 group-hover:text-blue-300 dark:group-hover:text-blue-200 transition-colors" aria-hidden="true">
            <path d="M12 6v6l4 2"></path>
            <circle cx="12" cy="12" r="10"></circle>
          </svg>
        ),
        type: "hours",
        content: "평일 09:00 - 18:00"
      }
    ]
  },
  // 저작권·하단 영역 (FooterAreaManager.jsx getDefaultCopyright와 동일 구조)
  copyright: getDefaultCopyright()
};

const Footer = memo(({ className }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isAlliancePath =
    location.pathname.startsWith('/alliance') && !location.pathname.includes('alliance-list');
  const shouldSkipMenu = shouldSkipMenuFetch({ pathname: location.pathname, isAlliancePath });
  const { menuData } = useMenuData(shouldSkipMenu ? { skip: true } : {});

  const themeContext = useTheme();
  // window.__themeConfig를 우선 확인 (DB에서 로드한 값)
  const dbConfig = typeof window !== 'undefined' && window.__themeConfig ? window.__themeConfig : {};
  const themeConfig = themeContext?.themeConfig || dbConfig || {};
  const [footerContent, setFooterContent] = useState(null);
  const [rawFooterData, setRawFooterData] = useState(null);
  const [footerStatistics, setFooterStatistics] = useState(() => getFooterStatsFallback()); // eslint-disable-line no-unused-vars -- 백엔드 연동 시 useEffect에서 setFooterStatistics 사용

  const locale = i18nLangToThemeLocale(i18n.language);

  // 백엔드 연동 시: commApi에서 fetchFooterStats import 추가 후 아래 주석 해제 (GET /api/comm/footer-stats)
  // useEffect(() => {
  //   let cancelled = false;
  //   fetchFooterStats().then((data) => {
  //     if (!cancelled && Array.isArray(data)) setFooterStatistics(data);
  //   });
  //   return () => { cancelled = true; };
  // }, []);

  // DB 원본 푸터 데이터 로드 (workspace 변경 시만)
  useEffect(() => {
    const wid = Number(themeConfig.workspaceId || (typeof window !== 'undefined' && window.__themeConfig?.workspaceId)) || 1;
    let cancelled = false;
    setRawFooterData(null);
    (async () => {
      try {
        const doc = await fetchFooterContentDocumentWithFallback(wid);
        if (cancelled || !doc?.footerContent?.footerData) return;
        setRawFooterData(doc.footerContent.footerData);
      } catch (e) {
        console.warn('[Footer] DB 푸터 로드 생략:', e?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [themeConfig.workspaceId]);

  /** 시스템 > Footer 영역 관리에서 저장 시 DB 최신값으로 다시 병합 */
  useEffect(() => {
    const wid = Number(themeConfig.workspaceId || (typeof window !== 'undefined' && window.__themeConfig?.workspaceId)) || 1;

    const onFooterConfigSaved = async (ev) => {
      if (ev.detail?.workplaceId != null && ev.detail.workplaceId !== wid) return;
      try {
        const doc = await fetchFooterContentDocumentWithFallback(wid);
        if (!doc?.footerContent?.footerData) return;
        setRawFooterData(doc.footerContent.footerData);
      } catch (e) {
        console.warn('[Footer] glow-footer-config-saved 재병합 생략:', e?.message);
      }
    };

    window.addEventListener('glow-footer-config-saved', onFooterConfigSaved);
    return () => window.removeEventListener('glow-footer-config-saved', onFooterConfigSaved);
  }, [themeConfig.workspaceId]);

  // footerContent: locale 변경 시 DB 원본을 즉시 재펼침 (API 재호출 없음)
  useEffect(() => {
    const defaultFooterContent = {
      logo: {
        image: "/assets/images/logo/logo.png",
        alt: "logo",
        link: "/"
      },
      about: {
        description: "UI CMS for KWARE",
        bottomImgList: {
          type: "bottom-img-list",
          list: [
            {
              src: "/assets/images/icon-img/payment.png",
              href: "#",
              title: "관계기관1"
            }
          ]
        }
      },
      widgets: [
        {
          title: "산학연계 현장실습",
          type: "list",
          colClass: "col-lg-3 col-md-6 col-sm-6 col-12",
          widgetClass: "footer-widget-margin-1",
          items: [
            { text: "학생 서비스", link: "#" },
            { text: "대학 서비스", link: "#" },
            { text: "기업 서비스", link: "#" }
          ]
        },
        {
          title: "지산학 정보마당",
          type: "list",
          colClass: "col-lg-2 col-md-6 col-sm-6 col-12",
          items: [
            { text: "알림마당", link: "#" },
            { text: "참여마당", link: "#" }
          ]
        },
        {
          title: "Contact Us",
          type: "address",
          colClass: "col-lg-4 col-md-6 col-sm-6 col-12",
          widgetClass: "footer-widget-margin-2 footer-address",
          items: [
            { label: "주소: ", text: "서울특별시 구로구 디지털로33길" },
            { label: "연락처:", text: " (02) 1234-5678" },
            { label: "이메일: ", text: "kware@kware.com" }
          ]
        }
      ],
      copyright: {
        text: "All rights reserved. Copyright © KWARE Inc."
      },
      footerData: defaultFooterData,
      _footerLinkMode: 'menu'
    };

    const applyFooterData = (rawFd) => {
      const n = normalizeFooterDataForRuntime(rawFd, locale);
      const mergedFd = mergeFooterDataWithDefaults(defaultFooterData, n);
      if (n?.contact) {
        mergedFd.contact = attachFooterContactIcons(mergedFd.contact);
      }
      const linkMode = n?.linkSections && n.linkSections.length > 1 ? 'static' : 'menu';
      const next = {
        ...defaultFooterContent,
        footerData: mergedFd,
        _footerLinkMode: linkMode
      };
      setFooterContent(next);
      if (typeof window !== 'undefined') window.footerContent = next;
    };

    if (rawFooterData) {
      applyFooterData(rawFooterData);
      return;
    }

    const content =
      typeof window !== 'undefined' && window.footerContent
        ? { ...window.footerContent, _footerLinkMode: window.footerContent._footerLinkMode || 'menu' }
        : defaultFooterContent;
    setFooterContent(content);
    if (typeof window !== 'undefined') window.footerContent = content;
  }, [locale, rawFooterData]);

  // footer 테마 적용 (원본 footer.applyTheme과 동일)
  useEffect(() => {
    const applyFooterTheme = async () => {
      try {
        // ThemeConfig 초기화 완료 대기 (이미 완료되었으면 즉시 반환)
        const ThemeConfig = window.ThemeConfig;
        if (ThemeConfig && typeof ThemeConfig.waitForInitialization === 'function') {
          await ThemeConfig.waitForInitialization();
        } else if (typeof window !== 'undefined' && !window.__themeConfig) {
          // ThemeConfig.waitForInitialization이 없으면 짧은 시간 대기
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const footerArea = document.querySelector('.footer-area');
        if (footerArea) {
          // 기존 테마 클래스 제거
          footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant', 'footer-theme-glow');
          // 새로운 테마 클래스 추가 (DB에서 로드한 값을 우선 사용)
          const dbTheme = typeof window !== 'undefined' && window.__themeConfig ? window.__themeConfig.footerTheme : null;
          const theme = dbTheme || themeConfig.footerTheme || 'simple';
          footerArea.classList.add(`footer-theme-${theme}`);
        }
      } catch (error) {
        console.warn('[Footer] 테마 적용 중 오류:', error);
        // 오류 발생 시 기본값 사용
        const footerArea = document.querySelector('.footer-area');
        if (footerArea) {
          footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant', 'footer-theme-glow');
          footerArea.classList.add(`footer-theme-${themeConfig.footerTheme || 'simple'}`);
        }
      }
    };

    // 비동기로 테마 적용
    applyFooterTheme();
  }, [themeConfig.footerTheme]);

  // DB에서 로드한 값을 우선 사용 (훅은 early return 이전에 실행)
  const footerDataFromContent = footerContent?.footerData;
  const footerDataFromWindow = typeof window !== 'undefined' ? window.footerContent?.footerData : null;
  const baseData = footerDataFromWindow || footerDataFromContent || defaultFooterData;
  const dbTheme = typeof window !== 'undefined' && window.__themeConfig ? window.__themeConfig.footerTheme : null;
  const finalFooterTheme = dbTheme || themeConfig.footerTheme || 'simple';
  const isGlowTheme = finalFooterTheme === 'glow';

  const footerLinkMode = footerContent?._footerLinkMode ?? 'menu';

  const glowLinkSections = useMemo(() => {
    if (footerLinkMode === 'static' && Array.isArray(baseData.linkSections) && baseData.linkSections.length > 0) {
      return baseData.linkSections
        .map((s) => (isGlowQuickLinkSection(s) ? mergeLegalLinksIntoGlowQuickSection(s) : s))
        .filter((s) => s.items?.length > 0);
    }
    const savedFirst = baseData.linkSections?.[0];
    const quick =
      isGlowQuickLinkSection(savedFirst) && Array.isArray(savedFirst?.items) && savedFirst.items.length > 0
        ? mergeLegalLinksIntoGlowQuickSection(savedFirst)
        : DEFAULT_GLOW_QUICK_LINK_SECTION;
    const fromMenu = sliceGlowFooterSectionsByMenuRange(buildGlowFooterSectionsFromMenu(menuData));
    return [quick, ...fromMenu];
  }, [footerLinkMode, baseData.linkSections, menuData]);

  /** lg+: 1행 4열(빠른링크~연구사업) + 2행 4열(오픈~문의). 그 외 메뉴는 추가 행 */
  const glowFooterLinkBlocks = useMemo(() => {
    const filtered = glowLinkSections.filter((s) => s.items?.length > 0);
    return {
      row1: filtered.slice(0, 4),
      row2Links: filtered.slice(4, 7),
      overflow: filtered.slice(7)
    };
  }, [glowLinkSections]);

  const commonData = { ...baseData, statistics: footerStatistics };

  if (!footerContent) {
    return null;
  }

  const { logo, about, widgets, copyright } = footerContent || {};

  const glowFooterThemeBackground =
    isGlowTheme &&
    commonData.glowPlatform &&
    String(commonData.glowPlatform.themeBackgroundColor ?? '').trim() !== ''
      ? String(commonData.glowPlatform.themeBackgroundColor).trim()
      : undefined;

  return (
    <footer
      className={`footer-area ${className || `footer-theme-${finalFooterTheme}`}`}
      style={glowFooterThemeBackground ? { backgroundColor: glowFooterThemeBackground } : undefined}
    >
      {isGlowTheme ? (
        <>
          {/* 메인 푸터 콘텐츠 */}
          <div className="footer-main-content">
            <div className="container">
              <div className="row footer-glow-main-row g-4 g-lg-4 align-items-start">
                {/* 좌측: GLOW Platform (lg 이상에서 고정 폭) */}
                {commonData.glowPlatform && (
                  <div className="col-12 col-lg-4 col-xl-3">
                    <div className="footer-glow-brand-block">
                      {commonData.glowPlatform.logo && (
                        <div className="footer-glow-platform-logo-container">
                          <div
                            className="footer-glow-platform-logo"
                            style={{
                              width: `${commonData.glowPlatform.logo.size || 49}px`,
                              height: `${commonData.glowPlatform.logo.size || 49}px`,
                              backgroundColor: commonData.glowPlatform.logo.backgroundColor || 'rgb(147, 51, 234)',
                              border: commonData.glowPlatform.logo.border || '1px solid #ffffff33'
                            }}
                          >
                            <span
                              className="footer-glow-platform-logo-text"
                              style={{ fontSize: commonData.glowPlatform.logo.fontSize || '1.5rem' }}
                            >
                              {commonData.glowPlatform.logo.letter || 'G'}
                            </span>
                          </div>
                          <div className="footer-glow-platform-text-container">
                            {commonData.glowPlatform.title && (
                              <h3 className="footer-glow-platform-title">
                                {commonData.glowPlatform.title}
                              </h3>
                            )}
                            {commonData.glowPlatform.subtitle && (
                              <p className="footer-glow-platform-subtitle">
                                {commonData.glowPlatform.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {commonData.glowPlatform.description && (
                        <p className="footer-glow-platform-description">
                          {commonData.glowPlatform.description}
                        </p>
                      )}
                      {commonData.glowPlatform.badges && commonData.glowPlatform.badges.length > 0 && (
                        <div className="footer-glow-platform-badges-container">
                          {commonData.glowPlatform.badges.map((badge, index) => (
                            <div
                              key={index}
                              className="footer-glow-platform-badge"
                              style={{
                                backgroundColor: badge.backgroundColor || '#065f46',
                                color: badge.color || '#05df72',
                                border: badge.borderColor ? `1px solid ${badge.borderColor}` : '1px solid #05df72'
                              }}
                            >
                              {renderFooterGlowBadgeLeading(badge)}
                              <span className="footer-glow-platform-badge-text">{badge.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {Array.isArray(commonData.glowPlatform.partnerLogos) &&
                        commonData.glowPlatform.partnerLogos.filter(
                          (p) => p && isFooterPartnerLogoUrlSafe(p.imageUrl)
                        ).length > 0 && (
                          <div className="footer-glow-platform-partner-logos-shell">
                            <div className="footer-glow-platform-partner-logos">
                              {commonData.glowPlatform.partnerLogos
                                .filter((p) => p && isFooterPartnerLogoUrlSafe(p.imageUrl))
                                .map((pl, idx) => {
                                  const href = String(pl.link || '').trim();
                                  const safeSrc = String(pl.imageUrl).trim();
                                  const img = (
                                    <img
                                      src={safeSrc}
                                      alt={pl.alt != null ? String(pl.alt) : ''}
                                      className="footer-glow-platform-partner-logo-img"
                                      loading="lazy"
                                    />
                                  );
                                  return (
                                    <div key={`partner-logo-${idx}`} className="footer-glow-platform-partner-logo-item">
                                      {href ? (
                                        <a
                                          href={href}
                                          className="footer-glow-platform-partner-logo-link"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {img}
                                        </a>
                                      ) : (
                                        img
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* 우측: 빠른링크 ~ 문의 (중첩 row, lg마다 4열 × 2행 + 문의) */}
                <div
                  className={
                    commonData.glowPlatform
                      ? 'col-12 col-lg-8 col-xl-9 footer-glow-links-panel'
                      : 'col-12 footer-glow-links-panel'
                  }
                >
                  <div className="row g-4">
                    {glowFooterLinkBlocks.row1.map((section, i) => (
                      <div key={`glow-foot-r1-${section.title}-${i}`} className="col-12 col-sm-6 col-lg-3">
                        <GlowFooterLinkColumn section={section} />
                      </div>
                    ))}
                    {glowFooterLinkBlocks.row2Links.map((section, i) => (
                      <div key={`glow-foot-r2-${section.title}-${i}`} className="col-12 col-sm-6 col-lg-3">
                        <GlowFooterLinkColumn section={section} />
                      </div>
                    ))}
                    {commonData.contact && (
                      <div className="col-12 col-sm-6 col-lg-3">
                        <div className="footer-contact-container footer-glow-contact-in-grid">
                          {commonData.contact.title && (
                            <h4 className="footer-contact-title">{commonData.contact.title}</h4>
                          )}
                          {commonData.contact.items && commonData.contact.items.length > 0 && (
                            <ul className="footer-contact-list">
                              {commonData.contact.items.map((item, index) => (
                                <li
                                  key={index}
                                  className="footer-contact-list-item"
                                  style={{ alignItems: item.type === 'address' ? 'flex-start' : 'center' }}
                                >
                                  {item.icon && (
                                    <span
                                      className="footer-contact-icon"
                                      style={{ marginTop: item.type === 'address' ? '2px' : 0 }}
                                    >
                                      {item.icon}
                                    </span>
                                  )}
                                  {item.type === 'address' ? (
                                    <div className="footer-contact-address-container">
                                      {item.title && (
                                        <div className="footer-contact-address-title">{item.title}</div>
                                      )}
                                      {item.content && (
                                        <div className="footer-contact-content">{item.content}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="footer-contact-content">{item.content}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {glowFooterLinkBlocks.overflow.length > 0 && (
                    <div className="row g-4 mt-lg-2">
                      {glowFooterLinkBlocks.overflow.map((section, i) => (
                        <div key={`glow-foot-of-${section.title}-${i}`} className="col-12 col-sm-6 col-lg-3">
                          <GlowFooterLinkColumn section={section} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* GLOW 테마 Copyright 영역 */}
          <FooterBottom isGlowTheme={true} commonData={commonData} />
        </>
      ) : (
        /* 기존 테마 구조 (simple, modern, vibrant) */
      <div className="bg-gray-2">
        <div className="container">
          <div className="footer-top pt-80 pb-35">
            <div className="row">
              {/* S: About 섹션 (로고 + 설명 + 결제 이미지) */}
              <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                <div className="footer-widget footer-about mb-40">
                  {logo && (
                    <div className="footer-logo">
                      <Link to={logo.link || '/'}>
                        <img src={logo.image} alt={logo.alt || 'logo'} />
                      </Link>
                    </div>
                  )}
                  {about && about.description && (
                    <p>{about.description}</p>
                  )}
                  {about && about.bottomImgList && about.bottomImgList.type === 'bottom-img-list' && about.bottomImgList.list && (
                    <div
                      className="payment-img"
                      style={{
                        ...(about.bottomImgList.list.length >= 2 ? {
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '10px',
                          alignItems: 'center'
                        } : {})
                      }}
                    >
                      {about.bottomImgList.list.map((item, idx) => (
                        <a
                          key={idx}
                          href={item.href || '#'}
                          title={item.title}
                          style={{
                            display: 'inline-block',
                            textDecoration: 'none'
                          }}
                        >
                          <img
                            src={item.src}
                            alt={item.title || '관계기관'}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              verticalAlign: 'middle'
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* E: About 섹션 */}

              {/* S: 위젯 섹션들 */}
              {widgets && Array.isArray(widgets) && widgets.map((widget, idx) => {
                if (!widget.title || !widget.type || !widget.items) return null;

                return (
                  <div key={idx} className={widget.colClass || 'col-lg-3 col-md-6 col-sm-6 col-12'}>
                    <div
                      className={`footer-widget mb-40 ${
                        widget.type === 'list' ? 'footer-list' : ''
                      } ${
                        widget.type === 'address' ? 'footer-address' : ''
                      } ${widget.widgetClass || ''}`}
                    >
                      <h3 className="footer-title">{widget.title}</h3>
                      <ul>
                        {widget.items.map((item, itemIdx) => (
                          <li key={itemIdx}>
                            {widget.type === 'list' && item.text && item.link ? (
                              <a href={item.link}>{item.text}</a>
                            ) : widget.type === 'address' && item.text ? (
                              <>
                                {item.label && <span>{item.label}</span>}
                                <span>{item.text}</span>
                              </>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
              {/* E: 위젯 섹션들 */}
            </div>
          </div>
        </div>
      </div>
      )}
      {!isGlowTheme && (
        /* 기존 테마 Copyright 영역 */
        <FooterBottom isGlowTheme={false} copyright={baseData.copyright ?? copyright} />
            )}
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
